// MIDNYTE — legal / compliance pages populated with the seller's actual details.
import { Link } from "react-router-dom";

const CY = {
  name: "Cynalp Enterprises",
  address: "Melrose Villa No. 7, Bellandur, Devarabeesanahalli, Bangalore 560103",
  phone: "9180242519",
  email: "customercare@midnyte.in",
  grievance: "Prashanth K",
};

const Missing = ({ what }) => (
  <mark className="legal-missing" data-testid="legal-missing">
    &lt;REQUIRED FROM SELLER: {what}&gt;
  </mark>
);

const LegalShell = ({ title, updated, testid, children }) => (
  <main className="legal-page" data-testid={testid}>
    <p className="eyebrow">MIDNYTE / LEGAL</p>
    <h1>{title}</h1>
    <p className="legal-meta">Last updated: {updated}</p>
    <div className="legal-body">{children}</div>
    <p className="legal-back"><Link to="/">← Back to store</Link></p>
  </main>
);

export function Privacy() {
  return (
    <LegalShell title="PRIVACY POLICY" updated="20 September 2026" testid="privacy-page">
      <p>This policy describes what personal data MIDNYTE collects when you use this website, why we collect it, and the rights you have under the Digital Personal Data Protection Act, 2023 (India).</p>

      <h2>1. Who we are</h2>
      <p>MIDNYTE is operated by <b>{CY.name}</b>, {CY.address}. For any privacy question, contact our Grievance Officer <b>{CY.grievance}</b> at <a href={`mailto:${CY.email}`}>{CY.email}</a> or {CY.phone}.</p>

      <h2>2. Data we collect</h2>
      <ul>
        <li><b>Account</b>: your name, email, mobile number, and (when you save it) your Bangalore delivery address. Password is stored only as a bcrypt hash.</li>
        <li><b>Orders</b>: line items, subtotal, delivery charge and total we calculated on our server.</li>
        <li><b>UPI payment proof</b>: UTR / transaction reference, payment date-time, and the screenshot you upload for verification.</li>
        <li><b>Waitlist</b>: your email and the product you asked us to notify you about.</li>
        <li><b>Admin session cookie</b> <code>midnyte_admin</code>, <b>customer session cookie</b> <code>midnyte_user</code>: HttpOnly + Secure. Not shared with anyone.</li>
        <li><b>Cart</b>: stored on your browser in <code>localStorage</code> as <code>midnyte_cart</code>; never leaves your device unless you check out.</li>
        <li><b>PostHog product analytics</b>: page views and, if you consent via our cookie banner, anonymous session replays used to debug the storefront. Data is sent to <code>ap.emergent.sh</code>.</li>
        <li><b>Server logs</b>: request URL, HTTP status, IP address and timestamp, retained for a short operational window.</li>
        <li><b>Google Fonts</b>: web fonts are loaded from <code>fonts.googleapis.com</code>; Google may log your IP as part of that request.</li>
      </ul>
      <p>We do <b>not</b> collect card numbers, CVV, UPI PIN or bank login credentials. Payment is made through your own UPI app; we only store the UTR and the screenshot you send us for verification.</p>

      <h2>3. Why we process it</h2>
      <ul>
        <li>To fulfil your order and communicate about it (delivery, cancellation, restock alerts).</li>
        <li>To manually verify each UPI payment.</li>
        <li>To operate and secure the site (fraud prevention, error debugging, session security).</li>
        <li>To comply with tax, consumer-protection and record-keeping obligations.</li>
      </ul>

      <h2>4. Who we share it with</h2>
      <ul>
        <li>Local delivery partners strictly to deliver your order within Bangalore.</li>
        <li>PostHog Inc. (product analytics) via <code>ap.emergent.sh</code> as our data processor.</li>
        <li>Government authorities where required by law.</li>
      </ul>
      <p>We do <b>not</b> sell your personal data.</p>

      <h2>5. Retention</h2>
      <p>Order records are retained for as long as required by applicable tax and consumer-protection laws. Payment screenshots and UTRs are retained with the associated order. Waitlist emails are retained until you ask us to delete them or the product is permanently discontinued.</p>

      <h2>6. Your rights</h2>
      <p>You have the right to know, correct, delete or export your personal data, and to withdraw analytics consent at any time. To exercise these rights email <a href={`mailto:${CY.email}`}>{CY.email}</a>. We will respond within the timelines mandated by applicable law.</p>

      <h2>7. Cookies &amp; similar technologies</h2>
      <p>This site uses two strictly-necessary session cookies (<code>midnyte_admin</code> for signed-in admins, <code>midnyte_user</code> for signed-in customers) and one strictly-necessary <code>localStorage</code> entry (<code>midnyte_cart</code>). Analytics/session replay via PostHog is loaded but is opt-in via the cookie banner and can be revoked at any time from the footer &ldquo;Cookie Preferences&rdquo; link.</p>

      <h2>8. Changes</h2>
      <p>If we materially change this policy we will update the &ldquo;Last updated&rdquo; date and, where required, notify you before the change takes effect.</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="TERMS &amp; CONDITIONS" updated="20 September 2026" testid="terms-page">
      <p>These Terms govern your use of the MIDNYTE website and the purchase of any product from it.</p>

      <h2>1. Seller</h2>
      <p>Products are sold by <b>{CY.name}</b>, {CY.address}. Contact: {CY.phone} · <a href={`mailto:${CY.email}`}>{CY.email}</a>.</p>

      <h2>2. Orders &amp; acceptance</h2>
      <p>When you place an order you are making an offer to buy. We may accept or reject the offer at our discretion, for example if the item is out of stock, a pricing error is discovered, or your delivery address is outside our current Bangalore service area. Contract is formed only once we verify your UPI payment and mark the order as Confirmed. Order totals are recomputed on our server from the current catalogue price — any total shown on your device is indicative.</p>

      <h2>3. Price &amp; payment</h2>
      <p>All prices are in Indian Rupees (INR) and are inclusive of applicable taxes unless stated otherwise. Payment is <b>UPI only</b>. Cash-on-Delivery is not available. You pay via any UPI app to our supplied QR / UPI ID, then submit the UTR and payment screenshot for manual verification.</p>
      <p>MIDNYTE will <b>never</b> ask you for your UPI PIN, card number, CVV, OTP or banking password. Do not share these with anyone claiming to be from MIDNYTE.</p>

      <h2>4. Product descriptions &amp; imagery</h2>
      <p>We try to describe our pieces accurately. Colours may render slightly differently across screens. Small handmade variations are expected. Any &ldquo;LIMITED&rdquo;, &ldquo;LAST FEW&rdquo;, &ldquo;BESTSELLER&rdquo; and &ldquo;TRENDING&rdquo; labels reflect our internal categorisation and current stock levels; they are not third-party certifications. &ldquo;SOLD OUT&rdquo; means our system shows zero units available.</p>

      <h2>5. Discounts &amp; MRP</h2>
      <p>Where a struck-through price is shown next to the selling price, it represents the manufacturer&apos;s declared Maximum Retail Price (MRP), inclusive of all taxes. If no struck-through price appears, the selling price is the MRP.</p>

      <h2>6. Delivery, returns and cancellations</h2>
      <p>See our <Link to="/shipping">Shipping Policy</Link> and{" "}
        <Link to="/returns">Return, Refund &amp; Cancellation Policy</Link>. These form part of these Terms.</p>

      <h2>7. Prohibited use</h2>
      <p>Do not use this site to reverse engineer, scrape at scale, or manipulate stock or prices. The catalogue, imagery, brand marks and code are the property of the seller and may not be reused without written permission.</p>

      <h2>8. Liability</h2>
      <p>To the extent permitted by law, our aggregate liability for any order is limited to the amount you paid for that order. Nothing in these Terms limits any statutory rights you have as a consumer under the Consumer Protection Act, 2019.</p>

      <h2>9. Governing law &amp; disputes</h2>
      <p>These Terms are governed by the laws of India. Courts at <b>Bangalore</b> shall have exclusive jurisdiction. Consumer complaints may also be lodged on the National Consumer Helpline (1915) or <a href="https://consumerhelpline.gov.in" target="_blank" rel="noreferrer">consumerhelpline.gov.in</a>.</p>
    </LegalShell>
  );
}

export function Shipping() {
  return (
    <LegalShell title="SHIPPING POLICY" updated="20 September 2026" testid="shipping-page">
      <h2>Where we ship</h2>
      <p>MIDNYTE currently delivers only within <b>Bangalore</b> (Indian pincodes starting 560, 561 and 562). Orders with a delivery pincode outside this range cannot be accepted.</p>

      <h2>Delivery charge</h2>
      <p>Delivery charges are <b>calculated based on delivery distance</b> and confirmed by us before dispatch. We do not offer free shipping and there is no fixed flat rate.</p>

      <h2>Dispatch</h2>
      <p>Orders are dispatched within <b>2 business days</b> after we receive and manually verify your UPI payment. Delays may occur due to local operations or force-majeure events; we will keep you informed if that happens.</p>

      <h2>Tracking &amp; support</h2>
      <p>For delivery status contact {CY.phone} or <a href={`mailto:${CY.email}`}>{CY.email}</a>.</p>
    </LegalShell>
  );
}

export function Returns() {
  return (
    <LegalShell title="RETURN, REFUND &amp; CANCELLATION POLICY" updated="20 September 2026" testid="returns-page">
      <h2>Order cancellation</h2>
      <p>You can cancel an order any time before we mark it as Confirmed by contacting us at {CY.phone} or <a href={`mailto:${CY.email}`}>{CY.email}</a>. Once dispatched, an order can no longer be cancelled.</p>

      <h2>Returns</h2>
      <p>We accept returns of unused, unworn pieces in their original packaging within <b>2 days after delivery</b>. Because of hygiene norms, earrings and any pierced-body jewellery are not eligible for return unless received damaged.</p>

      <h2>Damaged / wrong item received</h2>
      <p>If your parcel arrives damaged or you receive the wrong SKU, please email us within 48 hours of delivery with photos of the piece and the outer packaging. We will arrange a free replacement or full refund.</p>

      <h2>Refunds</h2>
      <p>Approved refunds are issued to your UPI ID / bank account within <b>2 days after we receive and accept the returned goods</b>. The money can take an additional few working days to reach your account depending on your bank.</p>

      <h2>How to raise a return / refund</h2>
      <p>Email <a href={`mailto:${CY.email}`}>{CY.email}</a> or call {CY.phone} with your order ID (starts with <code>MDN-</code>) and reason. Our team will guide you through the return.</p>

      <h2>Return address</h2>
      <p>{CY.name}<br />{CY.address}</p>
    </LegalShell>
  );
}

export function Contact() {
  return (
    <LegalShell title="CONTACT US" updated="20 September 2026" testid="contact-page">
      <h2>Customer care</h2>
      <p>
        Email: <a href={`mailto:${CY.email}`}>{CY.email}</a><br />
        Phone: {CY.phone}
      </p>

      <h2>Registered office</h2>
      <p>{CY.name}<br />{CY.address}<br />GSTIN: <Missing what="GSTIN, if registered" /></p>

      <h2>Grievance Officer</h2>
      <p>In accordance with the Consumer Protection (E-Commerce) Rules 2020 and the IT Rules 2021, complaints may be addressed to:</p>
      <p>
        Name: <b>{CY.grievance}</b><br />
        Email: <a href={`mailto:${CY.email}`}>{CY.email}</a><br />
        Phone: {CY.phone}<br />
        Address: {CY.address}
      </p>
      <p>We will acknowledge every grievance within 48 hours and resolve within 30 days.</p>

      <h2>National Consumer Helpline</h2>
      <p>You can also reach out to the National Consumer Helpline on <b>1915</b> or via <a href="https://consumerhelpline.gov.in" target="_blank" rel="noreferrer">consumerhelpline.gov.in</a>.</p>
    </LegalShell>
  );
}
